import React, { createContext, useContext, useEffect } from 'react';
import {
  useOidcAccessToken,
  useOidc,
  useOidcUser,
  type OidcUserInfo,
  OidcUserStatus,
} from '@axa-fr/react-oidc';
import { setAccessToken } from '@/api/base.api';
import { oidcConfiguration } from '@/config/oidcConfig';
import { logger } from '@/utils/logger';
import { clearAllReMapSessionCaches } from '@/helpers/remapSessionCache.helpers';
import { clearAllSchemaMappingDrafts } from '@/helpers/schemaMappingDraftCache.helpers';

interface AuthContextType {
  accessToken: string;
  user: any | null;
  isAuthenticated: boolean;
  logout: () => void;
  signIn: () => void;
  isRetoolMode: boolean;
  oidcUserLoadingState: string;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
  isRetoolMode?: boolean;
  retoolToken?: string;
}

// OIDC-enabled version
const OidcAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const oidcAccessToken = useOidcAccessToken();
  const oidcAuth = useOidc();
  const oidcUserData = useOidcUser<OidcUserInfo>();

  const accessToken = oidcAccessToken.accessToken;
  const user = oidcUserData.oidcUser;
  const isAuthenticated = !!accessToken;
  const oidcUserLoadingState = oidcUserData.oidcUserLoadingState;
  const isLoading = oidcUserLoadingState === OidcUserStatus.Loading;

  const logout = () => {
    clearAllReMapSessionCaches();
    clearAllSchemaMappingDrafts();
    oidcAuth.logout(oidcConfiguration.post_logout_redirect_uri);
  };

  const signIn = () => {
    if (oidcAuth.login) {
      oidcAuth.login();
    } else {
      logger.error('OIDC login method not available');
    }
  };

  useEffect(() => {
    setAccessToken(accessToken || '');
  }, [accessToken]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        user,
        isAuthenticated,
        logout,
        signIn,
        isRetoolMode: false,
        oidcUserLoadingState,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Retool-only version (no OIDC hooks)
const RetoolAuthProvider: React.FC<AuthProviderProps> = ({
  children,
  retoolToken,
}) => {
  const accessToken = retoolToken;
  const user = null;
  const isAuthenticated = !!accessToken;
  const logout = () => {}; // No logout in Retool
  const signIn = () => {}; // No signIn needed in Retool
  const isRetoolMode = true;
  const oidcUserLoadingState = 'Loaded'; // Always loaded in Retool
  const isLoading = false; // Never loading in Retool

  useEffect(() => {
    setAccessToken(accessToken || 'no-token');
  }, [accessToken]);

  return (
    <AuthContext.Provider
      value={{
        accessToken: accessToken || 'no-token',
        user,
        isAuthenticated,
        logout,
        signIn,
        isRetoolMode,
        oidcUserLoadingState,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Main AuthProvider that chooses the right implementation
export const AuthProvider: React.FC<AuthProviderProps> = (props) => {
  if (props.isRetoolMode) {
    return <RetoolAuthProvider {...props} />;
  }
  return <OidcAuthProvider {...props} />;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
